import type { UniversalCamera } from "@babylonjs/core/Cameras/universalCamera";
import { Engine } from "@babylonjs/core/Engines/engine";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import type { Scene } from "@babylonjs/core/scene";
import { TextureGenerator } from "../assets/TextureGenerator";
import { InputManager } from "../core/InputManager";
import { LoadingTask } from "../core/LoadingTask";
import { Time } from "../core/Time";
import { EnemySpawner } from "../enemies/EnemySpawner";
import { LevelGenerator } from "../generation/LevelGenerator";
import {
  TileType,
  type EnemyKind,
  type LevelData,
  type PickupKind,
  type RoomKind,
} from "../generation/types";
import { PlayerCombat } from "../player/PlayerCombat";
import { PlayerController } from "../player/PlayerController";
import { PlayerHealth } from "../player/PlayerHealth";
import { PlayerInventory } from "../player/PlayerInventory";
import { VentilationSystem, selectVentMounts } from "../rendering/VentilationSystem";
import { LevelMeshBuilder } from "../rendering/LevelMeshBuilder";
import { DungeonTorchSystem } from "../rendering/DungeonTorchSystem";
import { LightingController } from "../rendering/LightingController";
import { MaterialFactory } from "../rendering/MaterialFactory";
import { ParticleFactory } from "../rendering/ParticleFactory";
import { createScene } from "../rendering/SceneFactory";
import { gridToWorld, worldToGrid } from "../rendering/worldCoordinates";
import { AudioSystem, type SoundId } from "../systems/AudioSystem";
import { DoorSystem } from "../systems/DoorSystem";
import { ExitSystem } from "../systems/ExitSystem";
import { PickupSystem } from "../systems/PickupSystem";
import { ProjectileSystem } from "../systems/ProjectileSystem";
import { SecretSystem } from "../systems/SecretSystem";
import type { CircleCollisionBody } from "../systems/CollisionSystem";
import { Automap } from "../ui/Automap";
import { ControlsMenu } from "../ui/ControlsMenu";
import { EndScreen } from "../ui/EndScreen";
import { Hud } from "../ui/Hud";
import { LoadingScreen } from "../ui/LoadingScreen";
import { MainMenu } from "../ui/MainMenu";
import { MobileControls } from "../ui/MobileControls";
import { MobileFullscreenPrompt } from "../ui/MobileFullscreenPrompt";
import { PauseMenu } from "../ui/PauseMenu";
import { SettingsMenu } from "../ui/SettingsMenu";
import { shouldPlayMenuButtonSound } from "../ui/uiSoundPolicy";
import {
  DEFAULT_SEED,
  MONSTERS_ENABLED,
  PLAYER_CONFIG,
  STORAGE_KEYS,
  hardwareScalingLevelForQuality,
  loadSettings,
  type GameSettings,
} from "./GameConfig";
import { GameState } from "./GameState";

const LOCAL_FORWARD = Vector3.Forward();

interface Runtime {
  readonly ventilation: VentilationSystem;
  readonly level: LevelData;
  readonly scene: Scene;
  readonly camera: UniversalCamera;
  readonly materials: MaterialFactory;
  readonly controller: PlayerController;
  readonly combat: PlayerCombat;
  readonly enemies: EnemySpawner;
  readonly doors: DoorSystem;
  readonly pickups: PickupSystem;
  readonly projectiles: ProjectileSystem;
  readonly particles: ParticleFactory;
  readonly lighting: LightingController;
  readonly torches?: DungeonTorchSystem;
  readonly exit?: ExitSystem;
  readonly health: PlayerHealth;
  readonly secret: SecretSystem;
  readonly enemyBlockers: CircleCollisionBody[];
  readonly doorOccupants: Vector3[];
  readonly listenerForward: Vector3;
}

export interface GameDebugApi {
  readonly start: (seed?: string, spawnMonsters?: boolean) => void;
  readonly equipRifle: () => void;
  readonly focusProp: (index?: number) => void;
  readonly focusVent: (index?: number) => void;
  readonly teleportTo: (room: RoomKind) => void;
  readonly showMap: () => void;
  readonly complete: () => void;
  readonly killPlayer: () => void;
  readonly damageEnemy: (kind: EnemyKind) => void;
  readonly focusEnemy: (kind: EnemyKind) => void;
  readonly focusEnemyById: (id: string) => void;
  readonly focusBoss: () => void;
  readonly focusTorch: (index?: number) => void;
  readonly focusDoor: (id?: string) => void;
  readonly openDoor: (id?: string) => void;
  readonly playSound: (id: SoundId) => void;
  readonly equipShotgun: () => void;
  readonly focusPickup: (kind: PickupKind) => void;
  readonly setInvulnerable: (enabled: boolean) => void;
  readonly getState: () => Readonly<{
    phase: string;
    mapOpen: boolean;
    inputMode: string;
    yaw: number | null;
    pitch: number | null;
    selectedWeapon: string;
    elapsedSeconds: number;
    loadingTimings: Readonly<Record<string, number>>;
    fps: number;
    health: number;
    pistolMagazine: number;
    shotgunMagazine: number;
    rifleMagazine: number;
    propCount: number;
    ventAngles: readonly number[];
    kills: number;
    secrets: number;
    playerX: number | null;
    playerZ: number | null;
    invisibleCollisionMeshes: number | null;
    torchCount: number | null;
    torchLightIntensities: readonly number[] | null;
    exitLocked: boolean | null;
    doors: readonly Readonly<{ id: string; state: string; y: number }>[];
    enemies: readonly Readonly<{
      id: string;
      kind: EnemyKind;
      state: string;
      health: number;
      dead: boolean;
    }>[];
  }>;
}

export class Game {
  private readonly canvas: HTMLCanvasElement;
  private readonly engine: Engine;
  private readonly input: InputManager;
  private readonly time = new Time();
  private readonly state: GameState;
  private readonly audio: AudioSystem;
  private readonly mainMenu: MainMenu;
  private readonly hud = new Hud();
  private readonly mobileControls: MobileControls;
  private readonly mobileFullscreenPrompt: MobileFullscreenPrompt;
  private readonly loadingScreen = new LoadingScreen();
  private readonly automap = new Automap();
  private readonly pauseMenu: PauseMenu;
  private readonly settingsMenu: SettingsMenu;
  private readonly controlsMenu: ControlsMenu;
  private readonly endScreen: EndScreen;
  private runtime?: Runtime;
  private settings: GameSettings;
  private automapOpen = false;
  private settingsReturn: "menu" | "pause" = "menu";
  private controlsReturn: "menu" | "pause" = "menu";
  private toxicDamageTimer = 0;
  private debugInvulnerable = false;
  private loading?: AbortController;
  private loadingTimings: Readonly<Record<string, number>> = {};
  private canContinue = false;
  private readonly neutralizedEnemyIds = new Set<string>();

  public constructor(private readonly root: HTMLElement) {
    this.root.className = "game-shell";
    this.canvas = document.createElement("canvas");
    this.canvas.className = "game-canvas";
    this.canvas.setAttribute("aria-label", "Игровое окно «Инфернального прорыва»");
    this.root.append(this.canvas);
    this.settings = loadSettings();
    let storedSeed = DEFAULT_SEED;
    try {
      storedSeed = localStorage.getItem(STORAGE_KEYS.activeSeed) ?? DEFAULT_SEED;
    } catch {
      /* Storage unavailable. */
    }
    this.state = new GameState(storedSeed);
    this.audio = new AudioSystem(this.settings);
    this.engine = new Engine(
      this.canvas,
      true,
      { preserveDrawingBuffer: false, stencil: true, disableWebGL2Support: false },
      false,
    );
    this.resizeRenderer();
    this.input = new InputManager(this.canvas);
    this.mobileControls = new MobileControls(this.input, this.root, () => {
      if (this.mobileControls.portrait) this.pause();
      this.syncInputSettings();
      this.resizeRenderer();
    });
    this.mobileFullscreenPrompt = new MobileFullscreenPrompt();
    this.syncInputSettings();
    this.loadingScreen.onCancel = () => {
      this.loading?.abort();
      this.loadingScreen.hide();
      this.returnToMenu();
    };
    this.automap.onClose = () => this.toggleAutomap(false);

    this.mainMenu = new MainMenu({
      onProceduralRun: () => void this.startProceduralRun(this.createSeed()),
      onContinue: () => this.resumeFromMenu(),
      onSettings: () => this.openSettings("menu"),
      onControls: () => this.openControls("menu"),
    });
    this.pauseMenu = new PauseMenu(
      () => this.resume(),
      () => this.openSettings("pause"),
      () => this.openControls("pause"),
      () => void this.restartCurrentRun(this.state.seed),
      () => this.returnToMenu(),
    );
    this.settingsMenu = new SettingsMenu(
      this.settings,
      (settings) => this.applySettings(settings),
      () => this.closeSettings(),
    );
    this.controlsMenu = new ControlsMenu(() => this.closeControls());
    this.endScreen = new EndScreen(
      () => void this.restartCurrentRun(this.state.seed),
      () => this.returnToMenu(),
    );
    this.mainMenu.setCanContinue(false);
    this.root.append(
      this.hud.element,
      this.automap.element,
      this.mainMenu.element,
      this.pauseMenu.element,
      this.settingsMenu.element,
      this.controlsMenu.element,
      this.endScreen.element,
      this.loadingScreen.element,
      this.mobileControls.element,
      this.mobileControls.orientationGuard,
      this.mobileFullscreenPrompt.element,
    );

    this.canvas.addEventListener("click", this.onCanvasClick);
    this.canvas.addEventListener("webglcontextlost", this.onBlur);
    this.root.addEventListener("pointerover", this.onUiHover);
    this.root.addEventListener("click", this.onUiClick);
    document.addEventListener("pointerlockchange", this.onPointerLockChange);
    document.addEventListener("visibilitychange", this.onVisibilityChange);
    window.addEventListener("blur", this.onBlur);
    window.addEventListener("resize", this.onResize);
    this.engine.runRenderLoop(this.frame);
  }

  public readonly debugApi: GameDebugApi = {
    start: (seed = DEFAULT_SEED, spawnMonsters = MONSTERS_ENABLED) =>
      void this.startProceduralRun(seed, spawnMonsters),
    equipRifle: () => {
      this.state.grantRifle();
    },
    focusProp: (index = 0) => {
      const runtime = this.runtime;
      const prop = runtime?.level.props?.[index];
      if (runtime && prop) {
        const world = gridToWorld(prop.cell, runtime.level.grid);
        this.focusWorldTarget(new Vector3(world.x, 1, world.z));
      }
    },
    focusVent: (index = 0) => {
      const runtime = this.runtime;
      if (!runtime) return;
      const mount = selectVentMounts(runtime.level)[index];
      if (!mount) return;
      const world = gridToWorld(mount.cell, runtime.level.grid);
      const target = new Vector3(
        world.x + mount.direction.x * 0.91,
        2.1,
        world.z + mount.direction.y * 0.91,
      );
      runtime.camera.position.set(
        target.x - mount.direction.x * 3,
        1.65,
        target.z - mount.direction.y * 3,
      );
      runtime.controller.lookToward(target.subtract(runtime.camera.position));
    },
    teleportTo: (room) => this.teleportTo(room),
    showMap: () => this.toggleAutomap(true),
    complete: () => this.completeMission(),
    killPlayer: () => this.damagePlayer(999),
    damageEnemy: (kind) => {
      const enemy = this.runtime?.enemies.enemies.find(
        (candidate) => candidate.spawn.kind === kind && !candidate.dead,
      );
      enemy?.receiveDamage(enemy.maximumHealth - 1, this.runtime?.camera.position);
    },
    focusEnemy: (kind) => {
      const enemy = this.runtime?.enemies.enemies.find(
        (candidate) => candidate.spawn.kind === kind && !candidate.dead,
      );
      if (enemy) this.focusWorldTarget(enemy.position);
    },
    focusEnemyById: (id) => {
      const enemy = this.runtime?.enemies.getById(id);
      if (enemy) this.focusWorldTarget(enemy.position);
    },
    focusBoss: () => {
      const proceduralBoss = this.runtime?.enemies.enemies.find(
        (enemy) => enemy.spawn.kind === "abyssTyrant" && !enemy.dead,
      );
      if (proceduralBoss) this.focusWorldTarget(proceduralBoss.position);
    },
    focusTorch: (index = 0) => this.focusTorch(index),
    focusDoor: (id) => this.focusDoor(id),
    openDoor: (id) => {
      this.runtime?.doors.doors
        .find((candidate) => id === undefined || candidate.spawn.id === id)
        ?.interact();
    },
    playSound: (id) => this.audio.play(id),
    equipShotgun: () => {
      this.state.hasShotgun = true;
      this.state.selectedWeapon = "shotgun";
      this.state.ammo.shotgun.magazine = 6;
      this.state.ammo.shotgun.reserve = Math.max(this.state.ammo.shotgun.reserve, 18);
    },
    focusPickup: (kind) => this.focusPickup(kind),
    setInvulnerable: (enabled) => {
      this.debugInvulnerable = enabled;
    },
    getState: () => ({
      phase: this.state.phase,
      mapOpen: this.automapOpen,
      inputMode: this.mobileControls.enabled ? "touch" : "mouse",
      yaw: this.runtime?.camera.rotation.y ?? null,
      pitch: this.runtime?.camera.rotation.x ?? null,
      selectedWeapon: this.state.selectedWeapon,
      elapsedSeconds: this.state.elapsedSeconds,
      loadingTimings: this.loadingTimings,
      fps: Math.round(this.engine.getFps()),
      health: this.state.health,
      pistolMagazine: this.state.ammo.pistol.magazine,
      shotgunMagazine: this.state.ammo.shotgun.magazine,
      rifleMagazine: this.state.ammo.rifle.magazine,
      propCount: this.runtime?.level.props?.length ?? 0,
      ventAngles:
        this.runtime?.scene.transformNodes
          .filter((n) => n.name.startsWith("vent-rotor-"))
          .map((n) => n.rotation.z) ?? [],
      kills: this.state.kills,
      secrets: this.state.secrets,
      playerX: this.runtime?.camera.position.x ?? null,
      playerZ: this.runtime?.camera.position.z ?? null,
      invisibleCollisionMeshes:
        this.runtime?.scene.meshes.filter(
          (mesh) => mesh.checkCollisions && (!mesh.isVisible || mesh.visibility <= 0),
        ).length ?? null,
      torchCount: this.runtime?.torches?.count ?? null,
      torchLightIntensities: this.runtime?.torches?.lightIntensities ?? null,
      exitLocked: this.runtime?.exit?.isLocked ?? null,
      doors:
        this.runtime?.doors.doors.map((door) => ({
          id: door.spawn.id,
          state: door.state,
          y: door.mesh.position.y,
        })) ?? [],
      enemies:
        this.runtime?.enemies.enemies.map((enemy) => ({
          id: enemy.spawn.id,
          kind: enemy.spawn.kind,
          state: enemy.state,
          health: enemy.health,
          dead: enemy.dead,
        })) ?? [],
    }),
  };

  public dispose(): void {
    this.loading?.abort();
    this.mobileControls.dispose();
    this.mobileFullscreenPrompt.dispose();
    this.canvas.removeEventListener("click", this.onCanvasClick);
    this.canvas.removeEventListener("webglcontextlost", this.onBlur);
    document.removeEventListener("pointerlockchange", this.onPointerLockChange);
    document.removeEventListener("visibilitychange", this.onVisibilityChange);
    window.removeEventListener("blur", this.onBlur);
    window.removeEventListener("resize", this.onResize);
    this.root.removeEventListener("pointerover", this.onUiHover);
    this.root.removeEventListener("click", this.onUiClick);
    this.input.dispose();
    this.audio.dispose();
    this.runtime?.materials.dispose();
    this.runtime?.scene.dispose();
    this.engine.stopRenderLoop(this.frame);
    this.engine.dispose();
  }

  private async startProceduralRun(seed: string, spawnMonsters = MONSTERS_ENABLED): Promise<void> {
    if (this.loading) return;
    const abort = new AbortController();
    this.loading = abort;
    const task = new LoadingTask(abort.signal, (label, percent) =>
      this.loadingScreen.setProgress(label, percent),
    );
    const start = performance.now();
    void this.audio.unlock().catch(() => undefined);
    this.toggleAutomap(false);
    this.state.phase = "loading";
    this.canContinue = false;
    this.input.setEnabled(false);
    if (document.pointerLockElement) document.exitPointerLock();
    this.disposeRuntime();
    this.hud.hide();
    this.mainMenu.hide();
    this.pauseMenu.hide();
    this.endScreen.hide();
    this.settingsMenu.hide();
    this.controlsMenu.hide();
    this.mobileControls.setVisible(false);
    this.loadingScreen.show();

    try {
      await task.stage("ГЕНЕРАЦИЯ КОМПЛЕКСА", 5);
      const level = await task.run("generation", () => new LevelGenerator().generate(seed));
      await this.startLevel(level, task, spawnMonsters);
      task.timings["total"] = Math.round(performance.now() - start);
      this.loadingTimings = { ...task.timings };
      this.loadingScreen.hide();
      this.state.phase = "playing";
      this.input.setEnabled(true);
      this.hud.show();
      this.mobileControls.setVisible(true);
      this.canContinue = true;
      this.mainMenu.setCanContinue(true);
      this.time.reset();
      if (document.hidden || this.mobileControls.portrait) this.pause();
    } catch (error) {
      this.disposeRuntime();
      this.state.phase = "menu";
      if (!abort.signal.aborted) {
        console.error("Unable to start procedural run", error);
        this.loadingScreen.showError();
      }
    } finally {
      this.loading = undefined;
    }
  }

  private async restartCurrentRun(seed: string): Promise<void> {
    await this.startProceduralRun(seed);
  }

  private async startLevel(
    level: LevelData,
    task: LoadingTask,
    spawnMonsters: boolean,
  ): Promise<void> {
    this.audio.setMusicMode("dungeon");
    this.endScreen.hide();
    this.pauseMenu.hide();
    this.settingsMenu.hide();
    this.controlsMenu.hide();
    this.mainMenu.hide();
    this.automap.hide();
    this.automapOpen = false;
    this.neutralizedEnemyIds.clear();
    const seed = level.seed;
    const activeEnemies = spawnMonsters ? level.enemies : [];
    this.state.reset(
      seed,
      activeEnemies.length,
      "procedural",
      level.doors.filter((door) => door.secret).length,
    );
    this.state.phase = "loading";
    await task.stage("ПОСТРОЕНИЕ ГЕОМЕТРИИ", 18);
    const context = createScene(this.engine, level);
    const materials = new MaterialFactory(context.scene, new TextureGenerator(seed));
    try {
      const levelMeshes = await task.run("geometry", () =>
        new LevelMeshBuilder(context.scene, materials).buildAsync(level, task),
      );
      await task.stage("ОСВЕЩЕНИЕ И ПРЕДМЕТЫ", 45);
      const systemsStart = performance.now();
      const torches = new DungeonTorchSystem(context.scene, level, materials);
      const ventilation = new VentilationSystem(context.scene, level, materials);
      const health = new PlayerHealth(this.state);
      const inventory = new PlayerInventory(this.state);
      const particles = new ParticleFactory(context.scene);
      const projectiles = new ProjectileSystem(context.scene, level.grid, (amount) =>
        this.damagePlayer(amount),
      );
      const secret = new SecretSystem();
      const controller = new PlayerController(context.camera, this.input, this.settings);
      const openingTarget = level.rooms.find((room) => room.id === "encounter")?.center;
      if (openingTarget) {
        const target = gridToWorld(openingTarget, level.grid);
        controller.lookToward(
          new Vector3(
            target.x - context.camera.position.x,
            0,
            target.z - context.camera.position.z,
          ),
        );
      }
      const pickups = new PickupSystem(
        level.pickups,
        context.scene,
        level.grid,
        materials,
        inventory,
        this.audio,
      );
      task.timings["environment"] = Math.round(performance.now() - systemsStart);
      await task.stage("ПРОТИВНИКИ И ВООРУЖЕНИЕ", 65);
      const actorsStart = performance.now();
      const enemies = new EnemySpawner(
        activeEnemies,
        seed,
        context.scene,
        level.grid,
        materials,
        this.audio,
        {
          damagePlayer: (amount) => this.damagePlayer(amount),
          launchProjectile: (origin, target, damage) => projectiles.spawn(origin, target, damage),
          onKilled: (enemy) => {
            if (!this.neutralizedEnemyIds.has(enemy.spawn.id)) {
              this.neutralizedEnemyIds.add(enemy.spawn.id);
              this.state.kills += 1;
            }
            if (enemy.spawn.kind === "abyssTyrant") {
              exit?.unlock();
              this.hud.showMessage("Тиран Бездны повержен — эвакуация разблокирована", 4);
            } else {
              this.hud.showMessage("Враг уничтожен");
            }
          },
        },
      );
      const doors = new DoorSystem(level.doors, context.scene, level.grid, materials, this.audio, {
        canOpen: (spawn) => !spawn.requiredKey,
        onLocked: (spawn) => {
          const keyNames = {
            blue: "синяя ключ-руна",
            yellow: "жёлтая печать",
            red: "красный ключ",
          };
          const label = spawn.requiredKey ? keyNames[spawn.requiredKey] : "неизвестный ключ";
          this.hud.showMessage(`Требуется: ${label}`, 2.5);
          this.audio.play("switch");
        },
      });
      const lighting = new LightingController(
        context.emergencyLight,
        levelMeshes.decorativeLights,
        false,
      );
      const exit =
        level.mode === "procedural"
          ? new ExitSystem(level.exit, level.grid, context.scene, this.audio, {
              initiallyLocked: activeEnemies.some((enemy) => enemy.kind === "abyssTyrant"),
              onBlocked: () => this.hud.showMessage("Сначала уничтожьте Тирана Бездны", 2.5),
              onComplete: () => this.completeMission(),
            })
          : undefined;
      const combat = new PlayerCombat(
        context.scene,
        context.camera,
        controller,
        this.input,
        this.state,
        this.hud,
        particles,
        this.audio,
        {
          onEnemyHit: (enemyId, damage, hitPoint) => {
            const enemy = enemies.getById(enemyId);
            if (enemy) enemy.receiveDamage(damage, context.camera.position);
            this.audio.play("enemy_impact", hitPoint);
          },
          onLoudShot: (position) => enemies.broadcastShot(position),
        },
      );

      this.runtime = {
        level,
        scene: context.scene,
        camera: context.camera,
        materials,
        controller,
        combat,
        enemies,
        doors,
        pickups,
        projectiles,
        particles,
        lighting,
        torches,
        exit,
        health,
        secret,
        ventilation,
        enemyBlockers: [],
        doorOccupants: [],
        listenerForward: Vector3.Forward(),
      };
      this.syncInputSettings();
      task.timings["actors"] = Math.round(performance.now() - actorsStart);
      torches.refreshReceivers(context.scene);
      await task.stage("ПОДГОТОВКА ПЕРВОГО КАДРА", 85);
      await task.run("firstFrame", async () => {
        context.scene.render();
        await task.waitFor(context.scene.whenReadyAsync());
        task.check();
        context.scene.render();
        combat.update(0, false);
      });
      try {
        localStorage.setItem(STORAGE_KEYS.activeSeed, seed);
      } catch {
        /* Private browsing / quota. */
      }
      this.automap.setLevel(level);
      this.automap.setStats(this.state);
      this.hud.showMessage(
        spawnMonsters
          ? "Спуск в Бездну начат — уничтожьте Тирана и найдите точку эвакуации"
          : "Исследуйте подземелье — найдите тайник и точку эвакуации",
        3.5,
      );
      this.time.reset();
      this.toxicDamageTimer = 0;
    } catch (error) {
      materials.dispose();
      context.scene.dispose();
      this.runtime = undefined;
      throw error;
    }
  }

  private readonly frame = (): void => {
    this.time.tick();
    const runtime = this.runtime;
    if (runtime && this.state.phase !== "loading") {
      if (this.state.phase === "playing") this.updatePlaying(runtime, this.time.deltaSeconds);
      else runtime.controller.update(this.time.deltaSeconds, false);
      this.hud.update(this.state, this.time.deltaSeconds);
      runtime.scene.render();
    }
  };

  private updatePlaying(runtime: Runtime, deltaSeconds: number): void {
    if (this.input.consumePressed("Tab")) this.toggleAutomap();
    if (this.input.consumePressed("Escape")) {
      this.pause();
      return;
    }
    if (
      !document.pointerLockElement &&
      !this.mobileControls.isActive &&
      !this.automapOpen &&
      !this.debugInvulnerable
    ) {
      runtime.controller.update(deltaSeconds, false);
      runtime.combat.update(0, false);
      this.hud.setPrompt("НАЖМИТЕ НА ИГРОВОЕ ОКНО — ВКЛЮЧИТЬ УПРАВЛЕНИЕ");
      return;
    }
    const enabled = !this.automapOpen;
    runtime.enemies.collectLivingCollisionBodies(runtime.enemyBlockers);
    runtime.controller.update(deltaSeconds, enabled, runtime.enemyBlockers);
    runtime.combat.update(deltaSeconds, enabled);
    runtime.lighting.update(deltaSeconds);
    runtime.torches?.update(deltaSeconds);
    runtime.ventilation.update(deltaSeconds);
    runtime.particles.update(deltaSeconds);
    runtime.enemies.update(deltaSeconds, runtime.camera.position, runtime.level.grid);
    runtime.projectiles.update(deltaSeconds, runtime.camera.position);
    runtime.doorOccupants.length = 0;
    runtime.doorOccupants.push(runtime.camera.position);
    runtime.enemies.collectLivingPositions(runtime.doorOccupants);
    runtime.doors.update(deltaSeconds, runtime.doorOccupants);
    for (const pickup of runtime.pickups.update(deltaSeconds, runtime.camera.position)) {
      this.hud.showMessage(pickup.message);
    }
    this.automap.visit(runtime.camera.position);
    if (this.automapOpen) {
      this.automap.draw(runtime.camera.position);
      this.automap.setStats(this.state);
    }
    this.state.elapsedSeconds += deltaSeconds;
    runtime.camera.getDirectionToRef(LOCAL_FORWARD, runtime.listenerForward);
    this.audio.update(deltaSeconds, runtime.camera.position, runtime.listenerForward);
    this.updateToxicDamage(runtime, deltaSeconds);
    this.updateInteraction(runtime, enabled);
  }

  private updateInteraction(runtime: Runtime, enabled: boolean): void {
    if (!enabled) {
      this.hud.setPrompt(undefined);
      this.mobileControls.setContext(this.state.hasShotgun || this.state.hasRifle);
      return;
    }
    const forward = runtime.listenerForward;
    const door = runtime.doors.nearestUsable(
      runtime.camera.position,
      forward,
      PLAYER_CONFIG.interactionRange,
    );
    const canUseExit = runtime.exit?.isUsable(
      runtime.camera.position,
      forward,
      PLAYER_CONFIG.interactionRange,
    );
    const prompt = canUseExit ? runtime.exit?.prompt : door ? "E — ИСПОЛЬЗОВАТЬ ДВЕРЬ" : undefined;
    const touchPrompt = prompt?.replace(/^E\s*[—–-]\s*/u, "");
    this.hud.setPrompt(this.mobileControls.enabled ? touchPrompt : prompt);
    this.mobileControls.setContext(this.state.hasShotgun || this.state.hasRifle, touchPrompt);
    if (!this.input.consumePressed("KeyE")) return;
    if (canUseExit) {
      runtime.exit?.activate();
      return;
    }
    if (door) {
      const result = door.interact();
      if (result.secretDiscovered && runtime.secret.discover(door.spawn.id)) {
        this.state.secrets += 1;
        this.hud.showMessage("Тайник обнаружен", 3);
        this.audio.play("secret_found");
      }
    }
  }

  private updateToxicDamage(runtime: Runtime, deltaSeconds: number): void {
    const cell = worldToGrid(
      runtime.camera.position.x,
      runtime.camera.position.z,
      runtime.level.grid,
    );
    if (runtime.level.grid.get(cell) !== TileType.Toxic) {
      this.toxicDamageTimer = 0;
      return;
    }
    this.toxicDamageTimer += deltaSeconds;
    if (this.toxicDamageTimer >= 0.8) {
      this.toxicDamageTimer = 0;
      this.damagePlayer(5);
      this.hud.showMessage("Воздействие охлаждающей жидкости");
    }
  }

  private damagePlayer(amount: number): void {
    const runtime = this.runtime;
    if (!runtime || this.state.phase !== "playing" || this.debugInvulnerable) return;
    const result = runtime.health.damage(amount);
    if (result.healthDamage <= 0 && result.armorDamage <= 0) return;
    this.hud.flashDamage();
    this.audio.play("player_hurt");
    if (result.killed) {
      this.canContinue = false;
      this.input.setEnabled(false);
      this.toggleAutomap(false);
      if (document.pointerLockElement) document.exitPointerLock();
      this.hud.hide();
      this.mobileControls.setVisible(false);
      this.automap.hide();
      this.endScreen.showDeath(this.state.elapsedSeconds, this.state.kills);
    }
  }

  private completeMission(): void {
    if (!this.runtime || this.state.phase === "complete") return;
    this.state.phase = "complete";
    this.canContinue = false;
    this.input.setEnabled(false);
    this.toggleAutomap(false);
    if (document.pointerLockElement) document.exitPointerLock();
    this.hud.hide();
    this.mobileControls.setVisible(false);
    this.automap.hide();
    this.endScreen.showComplete(
      this.state.elapsedSeconds,
      this.state.kills,
      this.state.totalEnemies,
      this.state.secrets,
      this.state.totalSecrets,
    );
  }

  private pause(): void {
    if (this.state.phase !== "playing") return;
    this.toggleAutomap(false);
    this.state.phase = "paused";
    this.input.setEnabled(false);
    this.pauseMenu.show();
    this.mobileControls.setVisible(false);
    if (document.pointerLockElement) document.exitPointerLock();
  }

  private resume(): void {
    if (!this.runtime || this.mobileControls.portrait || document.hidden || !this.canContinue)
      return;
    this.pauseMenu.hide();
    this.settingsMenu.hide();
    this.controlsMenu.hide();
    this.state.phase = "playing";
    this.input.setEnabled(true);
    this.time.reset();
    this.mobileControls.setVisible(true);
    if (!this.mobileControls.enabled) this.input.requestPointerLock();
  }

  private returnToMenu(): void {
    this.input.setEnabled(false);
    this.toggleAutomap(false);
    if (document.pointerLockElement) document.exitPointerLock();
    this.state.phase = "menu";
    this.hud.hide();
    this.mobileControls.setVisible(false);
    this.automap.hide();
    this.automapOpen = false;
    this.pauseMenu.hide();
    this.endScreen.hide();
    this.settingsMenu.hide();
    this.controlsMenu.hide();
    this.mainMenu.setCanContinue(Boolean(this.runtime) && this.canContinue);
    this.mainMenu.show();
    this.audio.setMusicMode("none");
  }

  private resumeFromMenu(): void {
    if (!this.runtime || !this.canContinue || this.mobileControls.portrait || document.hidden)
      return;
    this.mainMenu.hide();
    this.hud.show();
    this.mobileControls.setVisible(true);
    this.state.phase = "playing";
    this.input.setEnabled(true);
    this.time.reset();
    this.audio.setMusicMode("dungeon");
    if (!this.mobileControls.enabled) this.input.requestPointerLock();
  }

  private openSettings(origin: "menu" | "pause"): void {
    this.settingsReturn = origin;
    this.mainMenu.hide();
    this.pauseMenu.hide();
    this.settingsMenu.show();
  }

  private closeSettings(): void {
    this.settingsMenu.hide();
    if (this.settingsReturn === "pause") this.pauseMenu.show();
    else this.mainMenu.show();
  }

  private openControls(origin: "menu" | "pause"): void {
    this.controlsReturn = origin;
    this.mainMenu.hide();
    this.pauseMenu.hide();
    this.controlsMenu.show(this.mobileControls.enabled);
  }

  private closeControls(): void {
    this.controlsMenu.hide();
    if (this.controlsReturn === "pause") this.pauseMenu.show();
    else this.mainMenu.show();
  }

  private applySettings(settings: GameSettings): void {
    this.settings = settings;
    try {
      localStorage.setItem(STORAGE_KEYS.settings, JSON.stringify(settings));
    } catch {
      /* Storage unavailable. */
    }
    this.audio.applySettings(settings);
    this.runtime?.controller.applySettings(settings);
    this.syncInputSettings();
    this.resizeRenderer();
  }

  private toggleAutomap(force?: boolean): void {
    const runtime = this.runtime;
    if (!runtime || (force !== false && this.state.phase !== "playing")) return;
    this.automapOpen = force ?? !this.automapOpen;
    this.input.clear();
    this.mobileControls.setMapOpen(this.automapOpen);
    if (this.automapOpen) {
      this.automap.show(runtime.camera.position);
      this.automap.setStats(this.state);
      this.hud.element.classList.add("map-open");
    } else {
      this.automap.hide();
      this.hud.element.classList.remove("map-open");
    }
  }

  private teleportTo(roomId: RoomKind): void {
    const runtime = this.runtime;
    const room = runtime?.level.rooms.find((candidate) => candidate.id === roomId);
    if (!runtime || !room) return;
    const world = gridToWorld(room.center, runtime.level.grid);
    runtime.camera.position.set(world.x, PLAYER_CONFIG.cameraHeight, world.z);
    this.automap.visit(runtime.camera.position);
  }

  private focusDoor(id?: string): void {
    const runtime = this.runtime;
    const door = runtime?.doors.doors.find(
      (candidate) => id === undefined || candidate.spawn.id === id,
    );
    if (!runtime || !door) return;
    const edge = runtime.level.graph.edges.find(
      (candidate) => door.spawn.id === `${candidate.from}-${candidate.to}`,
    );
    const room = runtime.level.rooms.find((candidate) => candidate.id === edge?.to);
    const outward =
      door.spawn.orientation === "x"
        ? new Vector3(
            0,
            0,
            Math.sign(door.spawn.position.y - (room?.center.y ?? door.spawn.position.y - 1)),
          )
        : new Vector3(
            Math.sign(door.spawn.position.x - (room?.center.x ?? door.spawn.position.x - 1)),
            0,
            0,
          );
    runtime.camera.position.copyFrom(door.mesh.position.add(outward.scale(2.5)));
    runtime.camera.position.y = PLAYER_CONFIG.cameraHeight;
    runtime.controller.lookToward(door.mesh.position.subtract(runtime.camera.position));
  }

  private focusWorldTarget(target: Vector3): void {
    const runtime = this.runtime;
    if (!runtime) return;
    const targetCell = worldToGrid(target.x, target.z, runtime.level.grid);
    const offsets = [
      { x: 0, y: 2 },
      { x: 2, y: 0 },
      { x: 0, y: -2 },
      { x: -2, y: 0 },
      { x: 0, y: 1 },
      { x: 1, y: 0 },
      { x: 0, y: -1 },
      { x: -1, y: 0 },
    ];
    const vantage = offsets
      .map((offset) => ({ x: targetCell.x + offset.x, y: targetCell.y + offset.y }))
      .find((cell) => runtime.level.grid.isWalkable(cell));
    if (!vantage) return;
    const world = gridToWorld(vantage, runtime.level.grid);
    runtime.camera.position.set(world.x, PLAYER_CONFIG.cameraHeight, world.z);
    runtime.controller.lookToward(target.subtract(runtime.camera.position));
    this.automap.visit(runtime.camera.position);
  }

  private focusPickup(kind: PickupKind): void {
    const runtime = this.runtime;
    const pickup = runtime?.pickups.findPosition(kind);
    const room = runtime?.level.rooms.find((candidate) => candidate.id === pickup?.roomId);
    if (!runtime || !pickup || !room) return;
    const center = gridToWorld(room.center, runtime.level.grid);
    runtime.camera.position.set(center.x, PLAYER_CONFIG.cameraHeight, center.z);
    runtime.controller.lookToward(pickup.position.subtract(runtime.camera.position));
  }

  private focusTorch(index: number): void {
    const runtime = this.runtime;
    const target = runtime?.torches?.getViewTarget(index);
    if (!runtime || !target) return;
    runtime.camera.position.copyFrom(target.viewingPosition);
    runtime.camera.position.y = PLAYER_CONFIG.cameraHeight;
    runtime.controller.lookToward(target.position.subtract(runtime.camera.position));
    this.automap.visit(runtime.camera.position);
  }

  private createSeed(): string {
    const bytes = crypto.getRandomValues(new Uint32Array(2));
    return `BREACH-${(bytes[0] ?? 0).toString(36).toUpperCase()}-${(bytes[1] ?? 0).toString(36).toUpperCase()}`;
  }

  private disposeRuntime(): void {
    if (!this.runtime) return;
    this.runtime.materials.dispose();
    this.runtime.scene.dispose();
    this.runtime = undefined;
  }

  private readonly onPointerLockChange = (): void => {
    if (
      !document.pointerLockElement &&
      !this.mobileControls.isActive &&
      this.state.phase === "playing" &&
      !this.automapOpen
    ) {
      this.pause();
    }
  };

  private resizeRenderer(): void {
    // Babylon scales the CSS render size itself. Keep the automatic profile at
    // two thirds of the viewport without multiplying by the device pixel ratio.
    this.engine.setHardwareScalingLevel(hardwareScalingLevelForQuality(this.settings.quality));
    this.engine.resize();
  }

  private syncInputSettings(): void {
    this.mobileControls.setSensitivity(this.settings.touchSensitivity);
    const mobile = this.mobileControls.enabled;
    this.runtime?.controller.setVerticalLookLocked(mobile);
    this.runtime?.combat.setVerticalAutoAim(mobile);
    this.mobileFullscreenPrompt.sync(mobile);
  }

  private readonly onVisibilityChange = (): void => {
    if (document.hidden) {
      this.input.clear();
      this.pause();
    }
  };

  private readonly onBlur = (): void => {
    this.input.clear();
    this.pause();
  };

  private readonly onCanvasClick = (): void => {
    void this.audio.unlock().catch(() => undefined);
    if (this.state.phase === "playing" && !this.automapOpen && !this.mobileControls.enabled) {
      this.input.requestPointerLock();
    }
  };

  private readonly onResize = (): void => this.resizeRenderer();

  private readonly onUiHover = (event: PointerEvent): void => {
    if (shouldPlayMenuButtonSound(event.target)) this.audio.play("ui_hover");
  };

  private readonly onUiClick = (event: MouseEvent): void => {
    if (shouldPlayMenuButtonSound(event.target)) {
      void this.audio.unlock().catch(() => undefined);
      this.audio.play("ui_click");
    }
  };
}
